/**
 * _cacheBust.js
 * Shared helpers for beating the year-long `cacheControl: 31536000` stale-cache
 * that `upload-magazine-page` applies when writing to Supabase storage.
 *
 * Root cause:
 *   If a file at a given storage path was ever uploaded blank or broken, the
 *   browser locks in that stale content for a year. Subsequent re-uploads
 *   to the same path are ignored at the HTTP cache layer.
 *
 * Fix:
 *   Append `?v=<updated_at-ms>` to every storage URL we render. A new query
 *   string = a new cache key = a fresh fetch whenever the DB row is re-upserted.
 */

/**
 * Build a cache key from a row's `updated_at` timestamp.
 * Returns '' if updated_at is missing.
 */
export function buildCacheKey(updatedAt) {
  if (!updatedAt) return '';
  const ms = new Date(updatedAt).getTime();
  if (!ms || Number.isNaN(ms)) return '';
  return `?v=${ms}`;
}

/**
 * Append `cacheKey` to a single URL (no-op if URL is falsy or already has `?`).
 */
export function bustUrl(url, cacheKey) {
  if (!url || !cacheKey) return url;
  if (url.startsWith('data:')) return url;  // data URLs are self-contained, no cache-busting needed
  if (url.includes('?')) return url;
  return `${url}${cacheKey}`;
}

/**
 * Walk a canvas_json object and append `cacheKey` to every Supabase storage
 * URL found inside any `src` field. Returns a new object (immutable).
 *
 * Why this is needed:
 *   Fabric.js canvas JSON embeds image URLs inside its objects array
 *   (e.g. `{ type: 'image', src: 'https://.../storage/v1/object/public/...' }`).
 *   Those baked-in URLs survive every re-save, so stale cache keeps serving
 *   blank images forever unless we rewrite them on load.
 *
 * Implementation: stringify → regex-replace any public-storage URL that
 * doesn't already carry a query string → parse back. This catches `src`
 * regardless of which object type nests it (image, pattern, filter, etc.).
 */
export function bustCanvasJsonUrls(canvasJson, cacheKey) {
  if (!canvasJson || !cacheKey) return canvasJson;
  try {
    const str = JSON.stringify(canvasJson);
    // Match Supabase public-storage URLs with no query string yet.
    // Inside JSON, URLs sit between `"` quotes. `[^"?]+?` prevents crossing
    // quote boundaries and skips URLs that already have `?`.
    const rewritten = str.replace(
      /"(https?:\/\/[^"?]+?\/storage\/v1\/object\/public\/[^"?]+?)"/g,
      (_m, url) => `"${url}${cacheKey}"`,
    );
    return JSON.parse(rewritten);
  } catch (_) {
    return canvasJson;
  }
}
